import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ChevronRight } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';
import HeroImageEditor from '@/components/HeroImageEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentBlog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const heroImage = useHeroImage('student_blog');

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`);
      setBlogs(response.data);
    } catch (error) {
      console.error('Failed to load blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      <section className="py-16 min-h-screen relative" data-testid="student-blog-page">
        <HeroImageEditor pageName="student_blog" />
        {/* Chicago River & Architecture Background */}
        <div className="absolute top-0 left-0 right-0 h-64 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{
              backgroundImage: `url(${heroImage})`,
              filter: 'sepia(50%) saturate(110%) hue-rotate(-5deg)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#E85D4D]/10 to-[#2C1810]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-7xl retro-title mb-6" data-testid="blog-title">Student Blog</h1>
            <p className="text-2xl text-[#F4E4C1] font-semibold mb-3">Notes on Learning Acupuncture & TCM</p>
            <p className="text-lg text-[#E8934D] font-medium">From the heart of Chicago</p>
          </div>

          {loading ? (
            <div className="text-center" data-testid="loading-state">
              <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center cassette-card p-12 max-w-2xl mx-auto" data-testid="no-blogs">
              <p className="text-[#3D2817] text-xl">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <article key={blog.id} className="cassette-card overflow-hidden group" data-testid={`blog-card-${blog.id}`}>
                  {blog.image_url && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={blog.image_url}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        data-testid={`blog-image-${blog.id}`}
                      />
                    </div>
                  )}
                  <div className="p-6 space-y-4">
                    <div className="flex items-center space-x-2 text-sm text-[#6B4423]">
                      <Calendar className="w-4 h-4" />
                      <span data-testid={`blog-date-${blog.id}`}>{formatDate(blog.created_at)}</span>
                    </div>
                    <h2 className="text-2xl retro-title text-[#3D2817] line-clamp-2" data-testid={`blog-title-${blog.id}`}>
                      {blog.title}
                    </h2>
                    <p className="text-[#3D2817] line-clamp-3" data-testid={`blog-excerpt-${blog.id}`}>{blog.excerpt}</p>
                    <Link
                      to={`/blog/${blog.id}`}
                      className="inline-flex items-center text-[#6B4423] font-semibold hover:text-[#3D2817] transition-colors"
                      data-testid={`blog-read-more-${blog.id}`}
                    >
                      Read More <ChevronRight className="ml-1 w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}